// Tournaments page (#/tournaments, battle design 7.3): the three tiers as cards (rules, calendar, entry, sign-up),
// the sheet editor, the bracket of the event you are in, your match (both open sheets, your ban, your secret order,
// practice runs against the opponent, every game with a replay rebuilt from its seed and both orders), and your
// trophies and title. Everything runs on the server without you: an idle player is auto-banned for and fields the
// saved order. Data comes on request ({ t: 'tour' }) and after each game.
import { psecTabs, psecWrap } from './psec.ts';
import {
  TOUR, TOUR_TIERS, TOUR_PRIZES, TIER_IDS, nextStartFrom, levelFits, levelRange, TICKET_SOURCES, creatureProblem, sheetCreature, sheetPower, autoBan, available, fieldFrom, fieldSize, playGame, titleFor, prizeFor, BATTLE_RULES, REPLAYABLE_FROM, rulesOf,
  type TourTier, type TourDTO, type TierCard, type TourView, type MyMatch, type GameRec,
  type TourPrize,
} from '../shared/tournament.ts';
import { arenaLevel, type ArenaCreature } from '../shared/arena.ts';
import { arenaPower } from '../shared/power.ts';
import { practice, type PracticeSummary } from '../shared/plan.ts';
import { SPECIES } from '../shared/species.ts';
import { card, teamStrip } from './teampick.ts';
import { openTeamBuilder } from './teambuilder.ts';
import { CONFIG } from '../shared/config.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { spriteUrl, iconUrl, has } from './assets.ts';
import { esc, full, cname, left } from './fmt.ts';
import { logLine } from './hud.ts';
import { battleLogHtml } from './battlelog.ts';
import { synergyChips } from './synergy.ts';
import { synergyScaleOf } from '../shared/pvpfight.ts';
import { heldChip, heldLabel } from './held.ts';
import { BOARD, type BoardRow } from '../shared/board.ts';
import { boardPrep, type PrepUnit } from './boardview.ts';
import { mySprite } from './trainerart.ts';
import { boardOn, frontLabel, pickFrom, reachBadge, rowClick, rowToggle, rowsOut, setRow, shownRows, type RowPick } from './rows.ts';

export const TN = {
  data: null as TourDTO | null,
  /** The sheet being edited: its tier, creature ids in order, how to pay, and rows (the board; null: defaultRows). */
  edit: null as null | { tier: TourTier; ids: number[]; pay: 'ticket' | 'coins'; rows: RowPick },
  /** Your order being arranged for the next game (null: not editing), and its rows once set by hand. */
  order: null as number[] | null,
  orderRows: null as RowPick,
  /** Open game replays, by "tour:round:slot:game". */
  replays: new Set<string>(),
  /** Another match opened from the bracket. */
  other: null as MyMatch | null,
  practice: null as null | { key: string; r: PracticeSummary },
  /** The match screen is open. */
  room: false,
};

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindTournaments(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }
export function loadTournaments() { send({ t: 'tour' }); }
export function onTour(d: TourDTO) { TN.data = d; rerender(); }
export function onTourMatch(m: MyMatch) { TN.other = m; rerender(); }
/** A game of yours was played: a line in the hunt log, fresh data if the page is open. */
export function onTourGame(m: { text: string }) {
  logLine(esc(m.text), /won/.test(m.text) ? 'gold' : '');
  if (S.page === 'tournaments') loadTournaments();
}

const spName = (id: string) => SPECIES[id]?.name ?? id;
const TIER_HUE: Record<TourTier, string> = { grand: '#e7b74a', rising: '#59c49a', weekly: '#6fa8ff' };

/**
 * Each tier's emblem: a crowned shield, a cup before a rising sun, two crossed blades (art/rig3d/parts/tourney.ts; this SVG
 * is the fallback). The card's banner (`hero`) shows the large drawing (parts/tourney-lg.ts), rendered at each size the
 * banner shows it at (144, 120 or 96 px wide, codex.css .tn2-hero) and at twice that.
 */
function emblem(tier: TourTier, hero = false): string {
  const lg = `icon-tour-${tier}-lg`;
  if (hero && has(lg)) {
    const at = (w: number) => `art/${lg}@${w}.png ${w}w`;
    return `<img class="tn-emblem" src="art/${lg}@144.png" srcset="${at(96)}, ${at(120)}, ${at(144)}, ${spriteUrl(lg)} 192w, ${at(240)}, ${at(288)}" sizes="(max-width: 520px) 96px, (max-width: 1279px) 120px, 144px" alt="" aria-hidden="true">`;
  }
  if (has(`icon-tour-${tier}`)) return `<img class="tn-emblem" src="${spriteUrl(`icon-tour-${tier}`)}" alt="" aria-hidden="true">`;
  const c = TIER_HUE[tier];
  const glyph = tier === 'grand'
    ? '<path d="M22 30l4-9 6 6 6-6 4 9z" fill="currentColor"/><circle cx="26" cy="20" r="1.6" fill="currentColor"/><circle cx="32" cy="25" r="1.6" fill="currentColor"/><circle cx="38" cy="20" r="1.6" fill="currentColor"/>'
    : tier === 'rising'
      ? '<path d="M21 31a11 11 0 0 1 22 0z" fill="currentColor"/><path d="M32 14v4M22.5 18l2.6 3M41.5 18l-2.6 3M17 25.5l3.6 1.2M47 25.5l-3.6 1.2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
      : '<path d="M21 17l20 18M43 17L23 35" stroke="currentColor" stroke-width="3" stroke-linecap="round"/><path d="M19 15l4 1-1 4zM45 15l-4 1 1 4z" fill="currentColor"/>';
  return `<svg class="tn-emblem" viewBox="0 0 64 56" style="color:${c}" aria-hidden="true">
    <path d="M32 3l22 7v14c0 13-9 23-22 29C19 47 10 37 10 24V10z" fill="color-mix(in srgb, ${c} 18%, transparent)" stroke="${c}" stroke-width="2"/>
    ${glyph}<path d="M20 38h24" stroke="${c}" stroke-width="2" stroke-linecap="round" opacity=".7"/></svg>`;
}

export function tournamentsHead() {
  const d = TN.data;
  return {
    // Where tickets come from, as links to where they are earned.
    meta: d ? `<span class="tn-tix"><img src="${iconUrl('ticket')}" alt=""><b>${d.tickets}</b> Ticket${d.tickets === 1 ? '' : 's'}</span>` : '',
    actions: '<button class="btn small" data-act="tn-refresh">Refresh</button>',
  };
}

export function tournamentsPane(): string {
  const d = TN.data;
  if (!d) return '<p class="hint">Loading the tournaments…</p>';
  const soon = !d.enabled;
  // A battle room takes the whole page: getting your team ready, or your match.
  if (TN.edit && !soon) return teamRoom(TN.edit.tier);
  if (TN.room && d.view?.my) return matchRoom(d.view.my, d.view);
  // On a phone one card (or the bracket, or the trophies) at a time under tabs (client/psec.ts).
  const view = d.view ? viewHtml(d.view) : soon ? '' : '<section class="panel tn-none"><p class="hint">No tournament yet.</p></section>';
  const trophies = soon && !d.trophies.length ? '' : trophiesHtml(d);
  const secs = [
    ...d.cards.map((c) => ({ key: c.tier, title: TIER_TAB[c.tier] })),
    ...(view ? [{ key: 'view', title: d.view ? 'Bracket' : 'Now' }] : []),
    ...(trophies ? [{ key: 'trophies', title: 'Trophies' }] : []),
  ];
  const keys = secs.map((x) => x.key);
  return `${psecTabs('tn', secs)}<div class="tn2-cards">${d.cards.map((c) => psecWrap('tn', c.tier, keys, cardHtml(c, soon))).join('')}</div>
    ${view ? psecWrap('tn', 'view', keys, `${view}${TN.other ? otherHtml(TN.other) : ''}`) : TN.other ? otherHtml(TN.other) : ''}
    ${trophies ? psecWrap('tn', 'trophies', keys, trophies) : ''}`;
}

function rulePills(tier: TourTier): string {
  const def = TOUR_TIERS[tier];
  const p: string[] = [];
  if (def.bans.aura) p.push('<span class="pill bad">No Aura</span>');
  if (def.bans.bloodlineV) p.push('<span class="pill bad">No Bloodline V</span>');
  if (def.bans.legendary) p.push('<span class="pill bad">No legendaries</span>');
  if (def.bans.tablets) p.push('<span class="pill bad">No Rune Tablets</span>');
  if (def.levelCap) p.push(`<span class="pill amber">Creatures Lv ${def.levelCap} max</span>`);
  else p.push('<span class="pill amber">Full levels</span>');
  if (!def.bans.aura && !def.bans.legendary) p.push('<span class="pill amber">Everything allowed</span>');
  return p.join('');
}

function entryText(tier: TourTier): string {
  const e = TOUR_TIERS[tier].entry;
  const parts = [e.tickets ? `${e.tickets} Ticket` : '', e.coins ? `${full(e.coins)} coins` : ''].filter(Boolean);
  return parts.join(' or ');
}

/** Each tier's name on a phone's tab. */
const TIER_TAB: Record<TourTier, string> = { grand: 'Grand', rising: 'Rising Cup', weekly: 'Weekly Clash' };

/** How often each tier runs, for the card's banner. */
const TIER_LINE: Record<TourTier, { kicker: string }> = {
  grand: { kicker: 'Monthly · open rules' },
  rising: { kicker: 'Monthly · mid-month' },
  weekly: { kicker: 'Weekly · Saturdays' },
};

const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
const dayFmt = (ms: number) => new Date(ms).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

/** A place's charms as prize text: "3 h Aura Charm". */
const charmBits = (p: TourPrize) => [p.charms.aura ? `${p.charms.aura} h Aura Charm` : '', p.charms.catch ? `${p.charms.catch} h Catch Charm` : ''];

/** A tier as a tall card: a dark banner with the emblem, then the rules, the facts, the prizes and the state. */
function cardHtml(c: TierCard, soon: boolean): string {
  const def = TOUR_TIERS[c.tier];
  const now = serverNow();
  const t = S.trainer!;
  const lvOk = levelFits(def, t.level);
  const launch = TN.data?.launch ?? null;
  const e = def.entry;
  const fact = (icon: string, k: string, v: string, sub = '', cls = '') => `<div class="tn2-fact${cls ? ` ${cls}` : ''}"><span class="tn2-fi" aria-hidden="true">${icon}</span><span><small>${k}</small><b>${v}</b>${sub ? `<em>${sub}</em>` : ''}</span></div>`;
  const ticket = `<img src="${iconUrl('ticket')}" alt="">`;
  const cal = '<svg viewBox="0 0 16 16"><rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M2 6.5h12M5 1.5v3M11 1.5v3" stroke="currentColor" stroke-width="1.5"/></svg>';
  const lvIcon = lvOk ? '<svg viewBox="0 0 16 16"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2"/></svg>' : '<svg viewBox="0 0 16 16"><rect x="3" y="7" width="10" height="7" rx="1" fill="currentColor"/><path d="M5 7V5a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
  const facts = fact(ticket, 'Entry', `${e.tickets} Ticket`, e.coins ? `or ${full(e.coins)} coins` : 'no coin entry')
    // Coming soon: when it runs, and its first date once launch day is set.
    + (soon
      ? (launch == null ? fact(cal, 'When', esc(cap(def.when)), `${TOUR.hourUtc}:00 UTC`) : fact(cal, 'First event', dayFmt(nextStartFrom(c.tier, now, launch)), `${TOUR.hourUtc}:00 UTC · then ${esc(def.when)}`))
      : fact(cal, 'Next event', dayFmt(c.start), `${TOUR.hourUtc}:00 UTC · ${esc(def.when)}`))
    + fact(lvIcon, 'Level', levelRange(def), lvOk ? 'You qualify' : `You are Lv ${full(t.level)}`, lvOk ? 'ok' : 'no');
  const prize = (place: 1 | 2 | 3) => {
    const p = TOUR_PRIZES.find((x) => x.place === place)!;
    const bits = [p.title ? `the title <b>${esc(titleFor(c.tier, place as 1 | 2))}</b>` : '', p.tickets ? `${p.tickets} Ticket${p.tickets === 1 ? '' : 's'}` : '', ...charmBits(p), 'a trophy'].filter(Boolean);
    return `<li><span class="tn2-place p${place}">${place === 1 ? '1st' : place === 2 ? '2nd' : '3rd'}</span><span>${bits.join(' · ')}</span></li>`;
  };
  // Coming soon: the corner ribbon says it, so the card has no footer.
  let status = '', actions = '';
  if (!soon) {
    if (c.state === 'running') status = `<span class="tn2-state live">Running now</span><span>${c.entrants} trainers</span>`;
    else if (now < c.opensAt) status = `<span class="tn2-state">Sign-ups in ${left(c.opensAt - now)}</span>`;
    else status = `<span class="tn2-state open">Sign-ups open</span><span>close in <b>${left(c.start - now)}</b> · ${c.entrants} entered</span>`;
    const open = c.state === 'open' && now >= c.opensAt && now < c.start;
    actions = c.mine
      ? `<button class="btn small" data-act="tn-edit" data-id="${c.tier}"${open ? '' : ' disabled'}>Change team</button><button class="btn small" data-act="tn-withdraw" data-id="${c.tier}"${open ? '' : ' disabled'}>Withdraw</button>`
      : `<button class="btn primary" data-act="tn-edit" data-id="${c.tier}"${open && lvOk ? '' : ' disabled'}${lvOk ? '' : ` title="For trainer level ${levelRange(def).slice(3)}"`}>Enter</button>`;
  }
  return `<article class="panel tn2${soon ? ' soon' : ''}" data-tier="${c.tier}" style="--tc:${TIER_HUE[c.tier]}" aria-label="${esc(def.name)}${soon ? ', coming soon' : ''}">
    <div class="tn2-hero">
      ${soon ? '<span class="tn2-ribbon">Coming soon</span>' : ''}
      ${emblem(c.tier, true)}
      <span class="tn2-kicker">${TIER_LINE[c.tier].kicker}</span>
      <h3>${esc(def.name)}</h3>
    </div>
    <div class="tn2-body">
      <div class="tn2-facts">${facts}</div>
      <div class="tn2-chips">${rulePills(c.tier)}</div>
      <ul class="tn2-prizes" aria-label="Prizes">${prize(1)}${prize(2)}${prize(3)}</ul>
      ${c.mine ? `<p class="good-text tn-in">Entered · ${c.mine.pay === 'ticket' ? `${c.mine.fee} ticket` : `${full(c.mine.fee)} coins`}</p>` : ''}
      ${c.last ? `<p class="muted tn-last">Last champion: <b>${esc(c.last.champion)}</b></p>` : ''}
      ${soon ? '' : `<div class="tn2-foot"><div class="tn2-status">${status}</div>${actions ? `<div class="actions">${actions}</div>` : ''}</div>`}
    </div>
  </article>`;
}

// ---------- the sheet editor ----------
/** The room's top: back to the tournaments, the event's emblem and name, and what it shows on the right. */
function roomHead(tier: TourTier, back: string, title: string, right = ''): string {
  return `<div class="tn-rh"><button class="bv-btn" data-act="${back}">‹ Tournaments</button>${emblem(tier)}<div class="tn-rt"><small>${esc(TOUR_TIERS[tier].name)}</small><h3>${title}</h3></div><span class="spacer"></span>${right}</div>`;
}

/** Getting ready: pick the team you go in with (it stays for the whole event) and place it. */
function teamRoom(tier: TourTier): string {
  const e = TN.edit!;
  const t = S.trainer!;
  const def = TOUR_TIERS[tier];
  const picked = e.ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  const team = picked.map((c) => sheetCreature(tier, c));
  const cardRow = TN.data?.cards.find((x) => x.tier === tier);
  const rows = shownRows(team, e.rows);
  const board = boardOn() && team.length ? boardPrep({
    key: `tn-sheet:${tier}`,
    mine: team.map((c, i) => unit(c, rows[i])),
    theirs: [],
    labels: ['You', ''],
    scene: 'tournament',
    trainers: [mySprite(), null],
    onRows: (picks) => { if (TN.edit) { TN.edit.rows = picks; rerender(); } },
  }) : '';
  const pay = cardRow?.mine ? '' : `<div class="tn-pay" role="radiogroup" aria-label="Pay with">${def.entry.tickets ? `<label><input type="radio" name="tn-pay" data-tn-pay="ticket"${e.pay === 'ticket' ? ' checked' : ''}> <img src="${iconUrl('ticket')}" alt=""> ${def.entry.tickets} <span class="muted">(${TN.data?.tickets ?? 0})</span></label>` : ''}${def.entry.coins ? `<label><input type="radio" name="tn-pay" data-tn-pay="coins"${e.pay === 'coins' ? ' checked' : ''}> ${full(def.entry.coins)} coins</label>` : ''}</div>`;
  return `<section class="panel tn-room" style="--tc:${TIER_HUE[tier]}">
    ${roomHead(tier, 'tn-s-cancel', cardRow?.mine ? 'Your team' : 'Choose your team', `<div class="tn2-chips">${rulePills(tier)}</div>`)}
    ${board ? `<div class="tn-stage">${board}</div>` : ''}
    <div class="tn-sec"><h4>Team <span class="muted">${team.length}/${TOUR.sheet} · Power ${full(sheetPower(team))}</span></h4>${synergyChips(team)}<span class="spacer"></span><button class="btn small" data-act="tn-team">Change team</button></div>
    ${teamStrip(team, TOUR.sheet, 'tn-team')}
    <div class="tn-foot">${pay}<span class="spacer"></span><button class="bv-btn main" data-act="tn-s-save"${team.length === TOUR.sheet ? '' : ' disabled'}>${cardRow?.mine ? 'Save' : 'Enter'}</button></div>
  </section>`;
}

/** A sheet creature as the board places it. */
const unit = (c: ArenaCreature, row: BoardRow): PrepUnit => ({ id: c.id, species: c.species, level: c.level, aura: c.rolls.aura, row, held: c.held });

function face(c: ArenaCreature, extra = '', cls = '', row?: BoardRow) {
  return `<span class="ar-mon${c.rolls.aura ? ' aura' : ''}${cls ? ` ${cls}` : ''}${row === 'front' ? ' fr' : ''}" title="${esc(spName(c.species))} · Lv ${full(c.level)} (fights as ${arenaLevel(c.level)}) · Power (Arena) ${full(arenaPower(c))}${row ? ` · ${row === 'front' ? 'front row' : 'back row'}` : ''}"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt=""><span>Lv ${full(c.level)}</span>${extra}</span>`;
}

/** One open-sheet row: the creature with its rolls in plain words. */
function sheetRow(c: ArenaCreature, i: number, opts: { banned?: boolean; ban?: boolean; auto?: boolean } = {}) {
  const r = c.rolls;
  const talent = r.ivs.reduce((a, b) => a + b, 0);
  return `<li class="${opts.banned ? 'banned' : ''}">${face(c)}<span class="grow"><b>${i + 1}. ${esc(spName(c.species))}</b> <span class="muted">Lv ${full(c.level)} · Power ${full(arenaPower(c))}</span>
    <span class="muted tn-rolls">Talent ${talent}/186 · Quality ×${r.quality.toFixed(2)} · Bloodline ${CONFIG.bloodline[r.bloodline]?.name ?? 'I'}${r.aura ? ' · Aura' : ''}${c.stars ? ` · ${c.stars}★` : ''}${c.held ? ` · ${esc(heldLabel(c.held))}` : ''}</span></span>
    ${opts.banned ? '<span class="pill bad">Banned</span>' : opts.auto ? '<span class="pill">Auto-ban</span>' : ''}${opts.ban ? `<button class="btn small" data-act="tn-ban" data-id="${c.id}">Ban</button>` : ''}</li>`;
}

// ---------- the event ----------
function viewHtml(v: TourView): string {
  const def = TOUR_TIERS[v.tier];
  const head = `<div class="ar-l-head"><h3>${esc(def.name)} · ${new Date(v.start).toLocaleDateString([], { day: 'numeric', month: 'long' })}</h3><span class="muted">${v.entrants} trainers · ${v.rounds} rounds · ${v.state === 'done' ? `won by <b>${esc(v.champion ?? '')}</b>` : v.state === 'running' ? 'running' : v.state}</span></div>`;
  return `<section class="panel tn-event" style="--tc:${TIER_HUE[v.tier]}">${head}${v.my ? matchBanner(v.my, v) : ''}<h4>Bracket</h4>${bracketHtml(v)}</section>`;
}

const roundName = (r: number, rounds: number) => (r === rounds - 1 ? 'Final' : r === rounds - 2 ? 'Semi-finals' : r === rounds - 3 ? 'Quarter-finals' : `Round ${r + 1}`);

function bracketHtml(v: TourView): string {
  if (!v.bracket.length) return '<p class="hint">Bracket at start.</p>';
  const cols = Array.from({ length: v.rounds }, (_, r) => v.bracket.filter((m) => m.round === r));
  return `<div class="tn-bracket">${cols.map((ms, r) => `<div class="tn-col"><h5>${roundName(r, v.rounds)}</h5>${ms.length ? ms.map((m) => `<button class="tn-m" data-act="tn-open" data-id="${v.id}:${m.round}:${m.slot}">
      ${[0, 1].map((s) => `<span class="${m.winner === s ? 'w' : m.winner !== null ? 'l' : ''}"><b>${m.names[s] === null ? '<i class="muted">bye</i>' : esc(m.names[s]!)}</b>${m.titles?.[s] ? `<small>${esc(m.titles[s]!)}</small>` : ''}<em>${m.names[s] === null ? '' : m.wins[s]}</em></span>`).join('')}</button>`).join('') : '<p class="muted">Waiting on the round before.</p>'}</div>`).join('')}</div>`;
}

function gamesHtml(m: MyMatch, mine: 0 | 1 | -1): string {
  if (!m.games.length) return '';
  return `<ol class="tn-games">${m.games.map((g, i) => {
    const key = `${m.tour}:${m.round}:${m.slot}:${i}`;
    const won = mine >= 0 ? g.winner === mine : null;
    return `<li><div class="ar-l-head"><b>Game ${i + 1}</b> ${won === null ? `<span>${esc(m.names[g.winner])} won</span>` : `<b class="${won ? 'good-text' : 'bad-text'}">${won ? 'Won' : 'Lost'}</b>`}<span class="muted">${(g.ms / 1000).toFixed(0)} s${g.timedOut ? ', on time' : ''}</span><span class="spacer"></span><button class="btn small" data-act="tn-replay" data-id="${key}" aria-expanded="${TN.replays.has(key)}">${TN.replays.has(key) ? 'Hide replay' : 'Replay'}</button></div>
      <div class="tn-orders">${[0, 1].map((s) => { const team = g.orders[s].map((id) => m.sheets[s].find((x) => x.id === id)).filter((c): c is ArenaCreature => !!c); return `<div><span class="muted">${esc(m.names[s])}</span><div class="ar-team">${team.map((c, k) => face(c, `<i>${k + 1}</i>`, '', g.rows?.[s][g.orders[s].indexOf(c.id)])).join('')}</div>${synergyChips(team, rulesOf(g.v ?? 1).synergy ? {} : null, undefined, synergyScaleOf(rulesOf(g.v ?? 1).boardV) ?? null)}</div>`; }).join('')}</div>
      ${TN.replays.has(key) ? replayHtml(m, g, mine) : ''}</li>`;
  }).join('')}</ol>`;
}

/** A game rebuilt from its seed, both sheets and both orders: the same blows the server played. */
function replayHtml(m: MyMatch, g: GameRec, mine: 0 | 1 | -1): string {
  // Rebuilt on the rules it was played on (with or without synergies and held items); games from before rules 3 replay on today's.
  const old = (g.v ?? 1) < REPLAYABLE_FROM;
  const r = playGame(m.names, m.sheets, g.orders, g.seed, old ? BATTLE_RULES : g.v, g.rows);
  const labels: [string, string] = [mine === 0 ? 'You' : m.names[0], mine === 1 ? 'You' : m.names[1]];
  // A game from before those rules can't be rebuilt blow for blow: say so instead of flagging a mismatch.
  const note = old ? ' · replayed on today\'s rules' : r.winner === g.winner ? '' : ' · <span class="bad-text">mismatch, please report</span>';
  return `<p class="muted">Seed ${g.seed}${note}</p>${battleLogHtml(r, labels, mine === 1 ? 1 : 0, { scene: 'tournament', trainers: mine === 0 ? [mySprite(), null] : mine === 1 ? [null, mySprite()] : undefined })}`;
}

/** Your match on the tournaments page: who, the score, and the way in. */
function matchBanner(m: MyMatch, v: TourView): string {
  const me = m.side, them = (1 - m.side) as 0 | 1;
  if (!m.names[them]) return `<div class="tn-mb"><b>${roundName(m.round, v.rounds)}</b><span class="good-text">Bye</span></div>`;
  const w = [m.games.filter((g) => g.winner === 0).length, m.games.filter((g) => g.winner === 1).length];
  return `<div class="tn-mb"><b>${roundName(m.round, v.rounds)}</b><span>vs <b>${esc(m.names[them]!)}</b></span><span class="tn-score">${w[me]}-${w[them]}</span><span class="spacer"></span><button class="bv-btn main" data-act="tn-room">${m.winner === null && v.state === 'running' ? 'To the match' : 'View match'}</button></div>`;
}

/**
 * The match screen: both open teams, your ban, then the board. Teams are locked for the event; before each game you
 * place yours in secret while their placement from the last game shows. Away, your saved placement plays.
 */
function matchRoom(m: MyMatch, v: TourView): string {
  const now = serverNow();
  const me = m.side, them = (1 - m.side) as 0 | 1;
  const theirs = m.sheets[them], mine = m.sheets[me];
  const w = [m.games.filter((g) => g.winner === 0).length, m.games.filter((g) => g.winner === 1).length];
  const decided = m.winner !== null;
  const live = !decided && v.state === 'running';
  const myBan = m.bans[me], theirBan = m.bans[them];
  const auto = theirs.length > 1 ? autoBan(theirs) : null;
  const canBan = !m.bansLocked && live;
  const pips = (n: number) => `<span class="bv-pips">${Array.from({ length: TOUR.winsNeeded }, (_, k) => `<i class="${k < n ? '' : 'out'}"></i>`).join('')}</span>`;
  const vs = `<div class="tn-vs"><span class="me"><b>You</b>${pips(w[me])}</span><span class="tn-vt">${decided ? `<b class="${m.winner === me ? 'good-text' : 'bad-text'}">${m.winner === me ? 'Won' : 'Out'}</b>` : `Game ${m.games.length + 1}<small>${left(Math.max(0, (m.nextAt ?? now) - now))}</small>`}</span><span class="them">${pips(w[them])}<b>${esc(m.names[them] ?? '')}</b></span></div>`;
  // Their team: ban one before game 1.
  const banPick = myBan ?? (canBan ? auto : null);
  const theirTeam = `<div class="tn-sec"><h4>${esc(m.names[them] ?? '')}</h4></div><div class="tn-strip them">${theirs.map((c) => {
    const banned = m.bansLocked ? myBan === c.id : banPick === c.id;
    const tag = banned ? `<em class="tn-tag bad">${m.bansLocked ? 'Banned' : myBan === null ? 'Auto ban' : 'Ban'}</em>` : '';
    return canBan ? `<div class="tn-slot${banned ? ' ban' : ''}"><button class="tn-sb" data-act="tn-ban" data-id="${c.id}" aria-pressed="${banned}" title="Ban">${card(c, tag)}</button></div>` : `<div class="tn-slot${banned ? ' ban' : ''}">${card(c, tag)}</div>`;
  }).join('')}</div>`;
  // Your side: everyone you field, placed on the board.
  const banned = m.bansLocked ? theirBan : null;
  const ord = TN.order ?? m.myOrder ?? m.nextOrder;
  const field = available(mine, banned).map((c) => c.id).sort((a, b) => ord.indexOf(a) - ord.indexOf(b));
  const fieldTeam = field.map((id) => mine.find((x) => x.id === id)!).filter(Boolean);
  const rowMap = orderRows(m, field);
  const last = m.games[m.games.length - 1];
  const theirLast = last ? last.orders[them].map((id, k) => { const c = theirs.find((x) => x.id === id); return c ? unit(c, last.rows?.[them][k] ?? 'back') : null; }).filter((u): u is PrepUnit => !!u) : [];
  if (last && !last.rows) { const tm = last.orders[them].map((id) => theirs.find((x) => x.id === id)).filter((c): c is ArenaCreature => !!c); const dr = shownRows(tm, null); tm.forEach((c, k) => { const u = theirLast.find((x) => x.id === c.id); if (u) u.row = dr[k]; }); }
  const board = boardOn() && live && fieldTeam.length ? boardPrep({
    key: `tn-order:${m.tour}:${m.round}:${m.slot}`,
    mine: fieldTeam.map((c) => unit(c, rowMap.get(c.id) ?? 'back')),
    theirs: theirLast,
    theirsLast: !!theirLast.length,
    labels: ['You', m.names[them] ?? ''],
    scene: 'tournament',
    trainers: [mySprite(), null],
    onRows: (picks) => {
      TN.order ??= [...(m.myOrder ?? m.nextOrder)];
      TN.orderRows = { ...Object.fromEntries(orderRows(m, field)), ...picks };
      rerender();
    },
  }) : '';
  const myTeam = `<div class="tn-sec"><h4>You</h4>${synergyChips(fieldTeam)}</div><div class="tn-strip">${mine.map((c) => `<div class="tn-slot${banned === c.id ? ' ban' : ''}">${card(c, banned === c.id ? '<em class="tn-tag bad">Banned</em>' : '')}</div>`).join('')}</div>`;
  const state = TN.order ? '<span class="tn-tag">Not locked in</span>' : m.myOrder ? '<span class="tn-tag good">Locked in</span>' : '<span class="tn-tag">Saved placement</span>';
  const foot = live ? `<div class="tn-foot">${state}<span class="spacer"></span>${practiceHtml(m)}<button class="bv-btn" data-act="tn-practice">Practice</button>${TN.order ? '<button class="bv-btn" data-act="tn-o-reset">Undo</button>' : ''}<button class="bv-btn main" data-act="tn-o-send"${TN.order ? '' : ' disabled'}>Lock in</button></div>` : '';
  return `<section class="panel tn-room" style="--tc:${TIER_HUE[v.tier]}">
    ${roomHead(v.tier, 'tn-room-close', roundName(m.round, v.rounds))}
    ${vs}
    ${theirTeam}
    ${board ? `<div class="tn-stage">${board}</div>` : ''}
    ${foot}
    ${myTeam}
    ${gamesHtml(m, me)}
  </section>`;
}

/**
 * The rows your order would fight on (the board), by id: the ones you set here, else the server's next rows (your last
 * sent or played rows, else your sheet's), else defaultRows over the creatures listed.
 */
function orderRows(m: MyMatch, ids: readonly number[]): Map<number, BoardRow> {
  const sheet = m.sheets[m.side];
  const team = ids.map((id) => sheet.find((c) => c.id === id)).filter((c): c is ArenaCreature => !!c);
  const pick = TN.orderRows ?? (m.nextRows && m.nextRows.length === m.nextOrder.length ? pickFrom(m.nextOrder, m.nextRows) : null);
  const rows = shownRows(team, pick);
  return new Map(team.map((c, k) => [c.id, rows[k]]));
}

/** Practice: your order for the next game against their last order (or their sheet order) on practice seeds. */
function practiceHtml(m: MyMatch): string {
  const p = TN.practice;
  if (!p || !p.key.startsWith(`${m.tour}:${m.round}:${m.slot}:`)) return '';
  return `<p class="tn-prac"><b class="${p.r.wins * 2 >= p.r.runs ? 'good-text' : 'bad-text'}">${p.r.wins} of ${p.r.runs}</b> practice fights won against their ${m.games.length ? 'last placement' : 'team'}</p>`;
}

function runPractice(m: MyMatch) {
  const me = m.side, them = (1 - m.side) as 0 | 1;
  const myBan = m.bans[me] ?? autoBan(m.sheets[them]);
  const theirBanOnMe = m.bansLocked ? m.bans[them] : null;
  const mine = fieldFrom(TN.order ?? m.myOrder ?? m.nextOrder, m.sheets[me], theirBanOnMe).map((id) => m.sheets[me].find((c) => c.id === id)!);
  const lastGame = m.games[m.games.length - 1];
  const lastTheirs = lastGame?.orders[them];
  const theirIds = lastTheirs ?? available(m.sheets[them], myBan).slice(0, TOUR.field).map((c) => c.id);
  const theirs = theirIds.map((id) => m.sheets[them].find((c) => c.id === id)!).filter(Boolean);
  // Rows: yours as the order editor shows them; theirs as they stood last game (else defaultRows).
  const myRows = orderRows(m, mine.map((c) => c.id));
  const mineSide = { name: 'You', team: mine, rows: mine.map((c) => myRows.get(c.id)) };
  const theirSide = { name: m.names[them], team: theirs, rows: lastGame?.rows ? theirs.map((c) => lastGame.rows![them][lastGame.orders[them].indexOf(c.id)]) : undefined };
  // Practice defends with the higher seed, as the real game does.
  const r = me === 0 ? practice(theirSide, mineSide) : practice(mineSide, theirSide);
  const wins = me === 0 ? r.runs - r.wins : r.wins;
  TN.practice = { key: `${m.tour}:${m.round}:${m.slot}:${m.games.length}`, r: { ...r, wins } };
}

function otherHtml(m: MyMatch): string {
  return `<section class="panel tn-other"><div class="ar-l-head"><h4>${esc(m.names[0])} vs ${esc(m.names[1] || 'bye')}</h4><span class="spacer"></span><button class="btn small" data-act="tn-close">Close</button></div>
    <div class="tn-sides">${[0, 1].map((s) => `<div><h5>${esc(m.names[s] || 'bye')}</h5><ol class="tn-sheet">${m.sheets[s].map((c, i) => sheetRow(c, i, { banned: m.bansLocked && m.bans[1 - s] === c.id })).join('')}</ol></div>`).join('')}</div>
    ${gamesHtml(m, -1)}</section>`;
}

function trophiesHtml(d: TourDTO): string {
  const label = { 1: 'Champion', 2: 'Finalist', 3: 'Semi-finalist' } as const;
  const titles = [...new Set(d.trophies.filter((x) => x.place <= 2).map((x) => titleFor(x.tier, x.place as 1 | 2)))];
  return `<section class="panel tn-trophies"><h4>Your trophies</h4>
    ${d.trophies.length ? `<ul class="tn-tlist">${[...d.trophies].reverse().map((x) => `<li style="--tc:${TIER_HUE[x.tier]}">${emblem(x.tier)}<span><b>${label[x.place]}</b> · ${esc(TOUR_TIERS[x.tier].name)}<br><span class="muted">${new Date(x.at).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}</span></span></li>`).join('')}</ul>` : '<p class="hint">None yet.</p>'}
    ${titles.length ? `<div class="actions tn-titles"><span class="muted">Title shown in brackets:</span>${titles.map((tt) => `<button class="btn small${d.title === tt ? ' on' : ''}" data-act="tn-title" data-id="${esc(tt)}" aria-pressed="${d.title === tt}">${esc(tt)}</button>`).join('')}${d.title ? '<button class="btn small" data-act="tn-title" data-id="">None</button>' : ''}</div>` : ''}
    <p class="muted">Champion ${prizeFor(1)!.tickets} tickets · finalist ${prizeFor(2)!.tickets}</p></section>`;
}

/** Clicks on data-act="tn-…". */
export function tournamentsClick(el: HTMLElement): boolean {
  const act = el.dataset.act ?? '';
  if (!act.startsWith('tn-')) return false;
  const id = el.dataset.id ?? '';
  const d = TN.data;
  const v = d?.view;
  const m = v?.my;
  switch (act) {
    case 'tn-refresh': loadTournaments(); return true;
    case 'tn-edit': {
      const tier = id as TourTier;
      const c = d?.cards.find((x) => x.tier === tier);
      const def = TOUR_TIERS[tier];
      TN.edit = { tier, ids: c?.mine?.ids.slice() ?? [], pay: def.entry.tickets && (d?.tickets ?? 0) >= def.entry.tickets ? 'ticket' : def.entry.coins ? 'coins' : 'ticket', rows: c?.mine ? pickFrom(c.mine.ids, c.mine.rows) : null };
      break;
    }
    case 'tn-withdraw': send({ t: 'tourWithdraw', tier: id }); return true;
    case 'tn-s-cancel': TN.edit = null; break;
    case 'tn-room': TN.room = true; break;
    case 'tn-team': {
      if (!TN.edit) break;
      const tier = TN.edit.tier, lv = S.trainer!.level;
      openTeamBuilder({
        title: `${TOUR_TIERS[tier].name} team`, ids: TN.edit.ids, max: TOUR.sheet, unique: true, name: `tn:${tier}`, key: `${tier}|${lv}`,
        ok: (c) => !creatureProblem(tier, lv, c), asFought: (c) => sheetCreature(tier, c),
        onSave: (ids) => { if (TN.edit) { TN.edit.ids = ids; rerender(); } },
      });
      return true;
    }
    case 'tn-s-add': {
      const sel = document.querySelector('[data-tn-add]') as HTMLSelectElement | null;
      const cid = Number(sel?.value);
      if (TN.edit && cid && !TN.edit.ids.includes(cid) && TN.edit.ids.length < TOUR.sheet) TN.edit.ids.push(cid);
      break;
    }
    case 'tn-s-up': case 'tn-s-down': case 'tn-o-up': case 'tn-o-down': {
      const list = act.startsWith('tn-s') ? TN.edit?.ids : (TN.order ??= [...(m?.myOrder ?? m?.nextOrder ?? [])].filter((x) => !m?.bansLocked || x !== m?.bans[1 - m.side]));
      if (!list) break;
      const i = Number(id), j = act.endsWith('up') ? i - 1 : i + 1;
      if (j >= 0 && j < list.length) [list[i], list[j]] = [list[j], list[i]];
      break;
    }
    case 'tn-s-remove': TN.edit?.ids.splice(Number(id), 1); break;
    case 'tn-s-row': {
      const x = rowClick(id);
      if (!TN.edit || !x) break;
      const tier = TN.edit.tier;
      const sheet = TN.edit.ids.map((cid) => S.creatures.get(cid)).filter((c): c is CreatureDTO => !!c).map((c) => sheetCreature(tier, c));
      TN.edit.rows = setRow(sheet, TN.edit.rows, x.id, x.row);
      break;
    }
    case 'tn-s-save': {
      if (!TN.edit) break;
      const tier = TN.edit.tier;
      const sheet = TN.edit.ids.map((cid) => S.creatures.get(cid)).filter((c): c is CreatureDTO => !!c).map((c) => sheetCreature(tier, c));
      const ids = sheet.map((c) => c.id);
      const shown = shownRows(sheet, TN.edit.rows);
      const rows = rowsOut(ids, TN.edit.rows, new Map(ids.map((cid, k) => [cid, shown[k]])));
      send({ t: 'tourEnter', tier, ids, pay: TN.edit.pay, ...(rows ? { rows } : {}) });
      TN.edit = null; break;
    }
    case 'tn-o-row': {
      const x = rowClick(id);
      if (!m || !x) break;
      const ord = (TN.order ??= [...(m.myOrder ?? m.nextOrder)].filter((cid) => !m.bansLocked || cid !== m.bans[1 - m.side]));
      const sheet = m.sheets[m.side];
      const team = ord.map((cid) => sheet.find((c) => c.id === cid)).filter((c): c is ArenaCreature => !!c);
      const cur = orderRows(m, ord);
      TN.orderRows = setRow(team, Object.fromEntries(cur), x.id, x.row);
      break;
    }
    case 'tn-ban': if (m) send({ t: 'tourBan', tour: m.tour, id: Number(id) }); return true;
    case 'tn-o-send': {
      if (m && TN.order) {
        const banned = m.bansLocked ? m.bans[1 - m.side] : null;
        const ids = m.bansLocked ? fieldFrom(TN.order, m.sheets[m.side], banned) : TN.order;
        const rows = TN.orderRows ? rowsOut(ids, TN.orderRows, orderRows(m, ids)) : undefined;
        send({ t: 'tourOrder', tour: m.tour, ids, ...(rows ? { rows } : {}) });
      }
      TN.order = null; TN.orderRows = null; return true;
    }
    case 'tn-o-reset': TN.order = null; TN.orderRows = null; break;
    case 'tn-practice': if (m) runPractice(m); break;
    case 'tn-replay': if (TN.replays.has(id)) TN.replays.delete(id); else TN.replays.add(id); break;
    case 'tn-open': { const [tour, round, slot] = id.split(':'); send({ t: 'tourMatch', tour, round: Number(round), slot: Number(slot) }); return true; }
    case 'tn-close': TN.other = null; break;
    case 'tn-title': send({ t: 'tourTitle', title: id || null }); return true;
    default: return false;
  }
  rerender();
  return true;
}

/** The pay radio. */
export function tournamentsInput(el: HTMLInputElement | HTMLSelectElement): boolean {
  const p = el.dataset.tnPay;
  if (p && TN.edit) { TN.edit.pay = p === 'coins' ? 'coins' : 'ticket'; return true; }
  return false;
}

/** Help page section. */
export function tournamentsHelp(): string {
  const tiers = TIER_IDS.map((t) => { const d = TOUR_TIERS[t]; return `<b>${esc(d.name)}</b> (${esc(d.when)}, ${TOUR.hourUtc}:00 UTC; trainer ${levelRange(d)}; entry ${entryText(t)}): ${esc(d.blurb)}`; }).join('</li><li>');
  return `<h4>Tournaments</h4><p>${TN.data?.enabled ? '' : '<b>Coming soon</b>: the page shows the tiers and rules, and sign-ups open when tournaments launch. '}Three tournaments on a fixed calendar (Tournaments in the More menu). Sign-ups open a week ahead (six days for the Weekly Clash) and close at the start; withdraw before the start and the fee comes back, and an event with fewer than ${TOUR.minEntrants} entrants is called off and refunded.</p>
    <ul><li>${tiers}</li>
    <li><b>Format</b>: bring a team of ${TOUR.sheet} creatures (one of each species); it stays the same for the whole event. Ban 1 of your opponent's, field ${TOUR.field}. Teams are open: everyone can see every creature and its rolls. Single elimination, seeded by team Power, best of 3. Bans close when game 1 starts (${TOUR.banMs / 60_000} minutes into the round); each further game follows ${TOUR.gameGapMs / 60_000} minutes later, and a round lasts ${TOUR.roundMs / 60_000} minutes. After each game both placements are shown, and both players place again in secret.</li>
    <li><b>Never miss a match</b>: everything runs on the server. Send no ban and your opponent's strongest creature by Power (Arena) is banned for you; place nothing and your team fights as you last placed it.</li>
    <li><b>Games</b> are the Arena's battle. The higher seed defends, so a dead tie at time goes to them. Each game's seed is drawn on the server when it starts, and any game can be replayed from that seed and the two orders. Practice runs use separate practice seeds, so they show odds, never the result.</li>
    <li><b>Entry and prizes</b>: a Tournament Ticket or coins (no real money, no diamonds). Tickets come from the weekly quest chest, the Arena's top ${TICKET_SOURCES.arenaTop} and tournament prizes, and stay on your account. The champion and the finalist get a trophy, a title, tickets (${prizeFor(1)!.tickets} and ${prizeFor(2)!.tickets}) and Aura Charm hours (${prizeFor(1)!.charms.aura} and ${prizeFor(2)!.charms.aura}); both semi-finalists a trophy and ${prizeFor(3)!.charms.catch} h of Catch Charm. Charms come as 1 h Bag items and trade for coins only.</li></ul>`;
}
